// BCFWriter (§5). Vytváří standardní BCF strukturu (2.1 i 3.0) a zabalí do .bcfzip.
// Export nezávisí na interním DB modelu — generuje standardní markup/viewpoint XML,
// aby byl soubor otevřitelný v jiných BCF aplikacích (§42.18).
import { zipWrite } from "../parser/zip.js";
import { esc, el, tag, vec3XML } from "../parser/xml.js";

const XML_HEAD = '<?xml version="1.0" encoding="UTF-8"?>\n';

function dataURLToBytes(durl) {
  if (!durl || typeof durl !== "string") return null;
  const i = durl.indexOf(",");
  if (i < 0) return null;
  const bin = atob(durl.slice(i + 1));
  const out = new Uint8Array(bin.length);
  for (let k = 0; k < bin.length; k++) out[k] = bin.charCodeAt(k);
  return out;
}

function cameraXML(cam) {
  if (!cam) return "";
  const body = vec3XML("CameraViewPoint", cam.position) + vec3XML("CameraDirection", cam.direction) + vec3XML("CameraUpVector", cam.up)
    + (cam.type === "orthogonal" ? el("ViewToWorldScale", cam.viewToWorldScale != null ? cam.viewToWorldScale : 1) : el("FieldOfView", cam.fieldOfView != null ? cam.fieldOfView : 60));
  return tag(cam.type === "orthogonal" ? "OrthogonalCamera" : "PerspectiveCamera", body);
}

function componentXML(ref) { return tag("Component", "", { IfcGuid: ref.globalId }); }

function viewpointBCFV(vp, version) {
  const sel = (vp.selection || []).map(componentXML).join("");
  const exceptions = (vp.visibility && vp.visibility.exceptions || []).map(componentXML).join("");
  const defVis = vp.visibility ? (vp.visibility.defaultVisibility !== false) : true;
  const components = tag("Components",
    tag("Selection", sel) +
    tag("Visibility", exceptions ? tag("Exceptions", exceptions) : "", { DefaultVisibility: String(defVis) }));
  const clipping = (vp.clippingPlanes && vp.clippingPlanes.length)
    ? tag("ClippingPlanes", vp.clippingPlanes.map((cp) => tag("ClippingPlane", vec3XML("Location", cp.location) + vec3XML("Direction", cp.direction))).join(""))
    : "";
  const attrs = version === "3.0" ? { Guid: vp.id } : null;
  return XML_HEAD + tag("VisualizationInfo", components + cameraXML(vp.camera) + clipping, attrs);
}

function labelsXML(labels, version) {
  if (!labels || !labels.length) return "";
  if (version === "3.0") return tag("Labels", labels.map((l) => el("Label", l)).join(""));
  return labels.map((l) => el("Labels", l)).join("");
}

function commentXML(c) {
  return tag("Comment",
    el("Date", c.date) + el("Author", c.author) + el("Comment", c.text) +
    (c.viewpointId ? tag("Viewpoint", "", { Guid: c.viewpointId }) : ""),
    { Guid: c.id });
}

function vpRefXML(vp, version) {
  const inner = el("Viewpoint", "viewpoint-" + vp.id + ".bcfv") + (vp.snapshot ? el("Snapshot", "snapshot-" + vp.id + ".png") : "") + (vp.index != null ? el("Index", vp.index) : "");
  return tag(version === "3.0" ? "ViewPoint" : "Viewpoints", inner, { Guid: vp.id });
}

function markupXML(topic, version) {
  const topicInner =
    el("Title", topic.title) +
    (topic.priority ? el("Priority", topic.priority) : "") +
    (topic.stage ? el("Stage", topic.stage) : "") +
    labelsXML(topic.labels, version) +
    el("CreationDate", topic.createdAt) +
    el("CreationAuthor", topic.createdBy) +
    (topic.modifiedAt ? el("ModifiedDate", topic.modifiedAt) : "") +
    (topic.modifiedBy ? el("ModifiedAuthor", topic.modifiedBy) : "") +
    (topic.dueDate ? el("DueDate", topic.dueDate) : "") +
    (topic.assignedTo ? el("AssignedTo", topic.assignedTo) : "") +
    (topic.description ? el("Description", topic.description) : "");

  const comments = (topic.comments || []).map(commentXML).join("");
  const viewpoints = (topic.viewpoints || []).map((vp) => vpRefXML(vp, version)).join("");

  let markupInner;
  if (version === "3.0") {
    // 3.0 — Comments a Viewpoints uvnitř Topicu.
    const topicEl = tag("Topic",
      topicInner + (comments ? tag("Comments", comments) : "") + (viewpoints ? tag("Viewpoints", viewpoints) : ""),
      { Guid: topic.id, TopicType: topic.type, TopicStatus: topic.status });
    markupInner = topicEl;
  } else {
    // 2.1 — Comment a Viewpoints jako sourozenci Topicu.
    const topicEl = tag("Topic", topicInner, { Guid: topic.id, TopicType: topic.type, TopicStatus: topic.status });
    markupInner = topicEl + comments + viewpoints;
  }
  return XML_HEAD + tag("Markup", markupInner);
}

function versionXML(version) {
  return XML_HEAD + tag("Version", el("DetailedVersion", version), { VersionId: version });
}

// Sestaví mapu souborů a vrátí .bcfzip jako Uint8Array.
export async function writeBCFArchive(project, version = "3.0") {
  const files = new Map();
  files.set("bcf.version", versionXML(version));
  files.set("project.bcfp", XML_HEAD + tag("ProjectExtension", tag("Project", el("Name", project.name || "BCF projekt"), { ProjectId: project.projectId })));

  for (const topic of project.topics || []) {
    const dir = topic.id;
    files.set(dir + "/markup.bcf", markupXML(topic, version));
    for (const vp of topic.viewpoints || []) {
      files.set(dir + "/viewpoint-" + vp.id + ".bcfv", viewpointBCFV(vp, version));
      if (vp.snapshot && vp.snapshot.data) {
        const bytes = dataURLToBytes(vp.snapshot.data);
        if (bytes) files.set(dir + "/snapshot-" + vp.id + ".png", bytes);
      }
    }
  }
  return await zipWrite(files);
}
